import type { ReactNode } from 'react'
import { Info } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { addBusinessDays, axisTicks, todayNumber } from '@/features/projects/project-expected-timeline-model'
import { cn } from '@/lib/utils'

/** The project and PO timelines' hues: with the manufacturer, then in transit (USA-1432). */
const PROCESSING = 'bg-zinc-300 dark:bg-zinc-600'
const LEAD = 'bg-sky-600 dark:bg-sky-500'
const TRANSIT = 'bg-sky-300 dark:bg-sky-700'
const TRANSIT_PENDING = 'border border-dashed border-sky-400 dark:border-sky-600'

function businessDays(count: number) {
  return `${count} business day${count === 1 ? '' : 's'}`
}

export type TimelineStep = {
  key: 'processing' | 'lead' | 'transit'
  label: string
  /** Business days; null while unknown (no destination yet). */
  days: number | null
  start: number
  end: number | null
  /** One short line under the days: where the number comes from. */
  source: string
  /** The date this step ends on, e.g. "Ships Tue, Nov 10". */
  date: string | null
  /** The hover explanation. */
  explanation: ReactNode
}

const SWATCH: Record<TimelineStep['key'], string> = {
  processing: PROCESSING,
  lead: LEAD,
  transit: TRANSIT,
}

function StepTooltip({ step, children }: { step: TimelineStep; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-72 text-left">
        <p className="mb-0.5 font-medium">{step.label}</p>
        <div className="opacity-90">{step.explanation}</div>
      </TooltipContent>
    </Tooltip>
  )
}

/**
 * Today to arrival as one bar, drawn like the project and PO expected timelines: our processing
 * day, the manufacturer's lead time, then the carrier's transit. Each part explains itself on hover.
 */
export function ProductDeliveryTimeline({
  steps,
  arrival,
  today = todayNumber(),
}: {
  steps: TimelineStep[]
  /** Arrival day once a destination is quoted. */
  arrival: number | null
  today?: number
}) {
  const ships = steps.find((step) => step.key === 'lead')?.end ?? today
  // Without a destination, leave room for a typical week in transit.
  const last = arrival ?? addBusinessDays(ships, 5)
  const start = today
  const end = last + Math.max(2, Math.round((last - start) * 0.06))
  const span = Math.max(1, end - start)
  const place = (day: number) => ((day - start) / span) * 100
  const ticks = axisTicks(start, end).filter((tick) => tick.left > 6)

  return (
    <TooltipProvider>
      <div className="space-y-3" data-testid="product-delivery-timeline">
        <div>
          <div className="relative h-8" aria-hidden>
            <span className="absolute top-0 whitespace-nowrap px-1 text-[11px] font-medium leading-4 text-foreground">
              Today
            </span>
            {ticks.map((tick) => (
              <span
                key={tick.day}
                className={cn(
                  'absolute top-4 whitespace-nowrap border-l pl-1 text-[11px] leading-4 tabular-nums text-muted-foreground',
                  (tick.left > 88 || !tick.major) && 'max-sm:hidden',
                  tick.left > 88 && 'hidden',
                )}
                style={{ left: `${tick.left}%` }}
              >
                {tick.label}
              </span>
            ))}
          </div>
          <div className="relative h-7">
            <div aria-hidden className="pointer-events-none absolute inset-0">
              {ticks.map((tick) => (
                <span
                  key={tick.day}
                  className="absolute inset-y-0 border-l border-border/70"
                  style={{ left: `${tick.left}%` }}
                />
              ))}
              <span className="absolute inset-y-0 left-0 w-px bg-foreground/60" />
            </div>
            {steps.map((step) => {
              const stepEnd = step.end ?? (step.key === 'transit' ? last : null)
              if (stepEnd === null) return null
              return (
                <StepTooltip key={step.key} step={step}>
                  <div
                    tabIndex={-1}
                    data-segment={step.key}
                    data-pending={step.end === null ? 'true' : undefined}
                    className={cn(
                      'absolute top-1/2 h-3.5 -translate-y-1/2 cursor-default',
                      step.end === null ? TRANSIT_PENDING : SWATCH[step.key],
                    )}
                    style={{
                      left: `${place(step.start)}%`,
                      width: `${Math.max(place(stepEnd) - place(step.start), 0.8)}%`,
                    }}
                  />
                </StepTooltip>
              )
            })}
            {steps.slice(1).map((step) => (
              <span
                key={`${step.key}-marker`}
                aria-hidden
                className="absolute top-1/2 h-3.5 w-0.5 -translate-x-1/2 -translate-y-1/2 bg-card"
                style={{ left: `${place(step.start)}%` }}
              />
            ))}
          </div>
        </div>

        <ol className="grid gap-x-6 gap-y-3 sm:grid-cols-3">
          {steps.map((step) => (
            <li key={step.key} className="min-w-0">
              <StepTooltip step={step}>
                <button
                  type="button"
                  className="group flex w-full min-w-0 flex-col items-start rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                >
                  <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <span aria-hidden className={cn('inline-block h-2.5 w-3.5', step.end === null && step.key === 'transit' ? TRANSIT_PENDING : SWATCH[step.key])} />
                    {step.label}
                    <Info className="size-3.5 opacity-60 group-hover:opacity-100" aria-hidden />
                  </span>
                  <span className="mt-1 text-sm font-semibold tabular-nums">
                    {step.days === null ? '—' : businessDays(step.days)}
                  </span>
                  <span className="text-xs text-muted-foreground">{step.source}</span>
                  {step.date ? <span className="text-xs tabular-nums text-muted-foreground">{step.date}</span> : null}
                </button>
              </StepTooltip>
            </li>
          ))}
        </ol>
      </div>
    </TooltipProvider>
  )
}
